import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import { Skeleton } from "@/components/ui/skeleton";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import PageBackground from "@/components/PageBackground";
import { toast } from "@/hooks/use-toast";

const BASE_URL = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/compressed-images`;

// Use WebP compressed images for faster loading
const CATEGORIES = [{
  id: "good-habits",
  label: "Good Habits",
  imageUrl: `${BASE_URL}/good-habits.webp`,
  subOptions: [{
    label: "Brushing Teeth Daily",
    imageUrl: `${BASE_URL}/brushing-teeth.webp`
  }, {
    label: "Cleaning My Room",
    imageUrl: `${BASE_URL}/cleaning-room.webp`
  }, {
    label: "Eating Healthy Foods",
    imageUrl: `${BASE_URL}/eating-healthy.webp`
  }, {
    label: "Going to Bed on Time",
    imageUrl: `${BASE_URL}/bedtime.webp`
  }]
}, {
  id: "social-behaviour",
  label: "Social Behaviour",
  imageUrl: `${BASE_URL}/social-behaviour.webp`,
  subOptions: [{
    label: "Sharing with Others",
    imageUrl: `${BASE_URL}/sharing.webp`
  }, {
    label: "Being Kind to Friends",
    imageUrl: `${BASE_URL}/being-kind.webp`
  }, {
    label: "Making New Friends",
    imageUrl: `${BASE_URL}/making-friends.webp`
  }, {
    label: "Saying Please & Thank You",
    imageUrl: `${BASE_URL}/please-thank-you.webp`
  }]
}, {
  id: "moral-stories",
  label: "Moral Stories",
  imageUrl: `${BASE_URL}/moral-stories.webp`,
  subOptions: [{
    label: "Honesty is the Best Policy",
    imageUrl: `${BASE_URL}/honesty.webp`
  }, {
    label: "Hard Work Pays Off",
    imageUrl: `${BASE_URL}/hard-work.webp`
  }, {
    label: "Respecting Elders",
    imageUrl: `${BASE_URL}/respecting-elders.webp`
  }, {
    label: "Being Brave and Courageous",
    imageUrl: `${BASE_URL}/being-brave.webp`
  }]
}];

// Collect all image URLs for preloading
const ALL_IMAGE_URLS = CATEGORIES.flatMap(cat => [
  cat.imageUrl,
  ...cat.subOptions.map(sub => sub.imageUrl)
]);

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubOption, setSelectedSubOption] = useState<string | null>(null);
  const [customDetails, setCustomDetails] = useState("");
  const [includeChild, setIncludeChild] = useState(true);
  const [childName, setChildName] = useState<string | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  
  // Subscription state
  const [currentPlan, setCurrentPlan] = useState<string>("free");
  const [storiesUsed, setStoriesUsed] = useState(0);
  const [storyLimit, setStoryLimit] = useState(3);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [freeStoriesRemaining, setFreeStoriesRemaining] = useState(3);
  
  const subOptionsRef = useRef<HTMLDivElement>(null);
  const customFormRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to subtopics when a category is selected
  useEffect(() => {
    if (selectedCategory && subOptionsRef.current) {
      setTimeout(() => {
        subOptionsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [selectedCategory]);

  // Auto-scroll to custom form when a subtopic is selected
  useEffect(() => {
    if (selectedSubOption && customFormRef.current) {
      setTimeout(() => {
        customFormRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [selectedSubOption]);

  // Check if we should show upgrade dialog (from CreateStory redirect)
  useEffect(() => {
    const state = location.state as { showUpgrade?: boolean } | null;
    if (state?.showUpgrade) {
      setShowUpgradeDialog(true);
      // Clear the state so it doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Preload all images on mount
  useEffect(() => {
    ALL_IMAGE_URLS.forEach(url => {
      const img = new Image();
      img.onload = () => {
        setLoadedImages(prev => new Set(prev).add(url));
      };
      img.src = url;
    });
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('child_name')
          .eq('id', user.id)
          .single();
        if (profile?.child_name) {
          setChildName(profile.child_name);
        }

        // Show welcome toast on fresh login (not on page refresh)
        const welcomeShownKey = `welcome_shown_${user.id}`;
        const lastWelcome = sessionStorage.getItem(welcomeShownKey);
        
        if (!lastWelcome) {
          sessionStorage.setItem(welcomeShownKey, Date.now().toString());
          toast({
            title: `Welcome${profile?.child_name ? `, ${profile.child_name}'s parent` : ''}! 👋`,
            description: "Ready to create some magical stories?",
          });
        }
      }
    };
    fetchProfile();
  }, []);

  // Fetch subscription data using get_effective_plan RPC
  useEffect(() => {
    const fetchSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Get effective plan (handles expiry automatically)
        const { data: planData } = await supabase.rpc('get_effective_plan', { user_uuid: user.id });
        
        if (planData && planData.length > 0) {
          const { plan, is_expired } = planData[0];
          setCurrentPlan(plan || 'free');
          setIsExpired(is_expired || false);
        }
        
        // Get effective story limit including carryover
        const { data: limitData } = await supabase.rpc('get_effective_story_limit', {
          user_uuid: user.id
        });
        if (limitData && limitData.length > 0) {
          setStoryLimit(limitData[0].total_limit);
        }
        
        // Get story count using RPC (already handles expiry)
        const { data: count } = await supabase.rpc('get_user_story_count', { user_uuid: user.id });
        setStoriesUsed(count || 0);
        
        // Get free stories remaining
        const { data: freeRemaining } = await supabase.rpc('get_free_stories_remaining', { user_uuid: user.id });
        setFreeStoriesRemaining(freeRemaining ?? 3);
      }
    };
    fetchSubscription();
  }, []);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
      setSelectedSubOption(null);
      setCustomDetails("");
    } else {
      setSelectedCategory(categoryId);
      setSelectedSubOption(null);
      setCustomDetails("");
    }
  };

  const handleSubOptionClick = (subOption: { label: string; imageUrl: string }) => {
    setSelectedSubOption(subOption.label);
  };

  const handleGenerateStory = () => {
    // Check if at limit (considering free stories)
    const canCreate = freeStoriesRemaining > 0 || (currentPlan !== 'free' && storiesUsed < storyLimit);
    if (!canCreate) {
      setShowUpgradeDialog(true);
      return;
    }
    
    const category = CATEGORIES.find(cat => cat.id === selectedCategory);
    navigate("/create", {
      state: {
        prefilledCategory: category?.label,
        prefilledSubOption: selectedSubOption,
        prefilledDetails: currentPlan !== 'basic' ? customDetails : "",
        includeChild: currentPlan !== 'basic' && includeChild,
        childName
      }
    });
  };

  const selectedCategoryData = CATEGORIES.find(cat => cat.id === selectedCategory);
  return <PageBackground>
      <Navbar />

      <main className="container mx-auto px-4 py-8 md:py-12 pb-24 md:pb-12">
        <div className="max-w-6xl mx-auto animate-fade-in">
          {/* Hero Section */}
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl lg:text-6xl font-semibold text-foreground mb-2 md:mb-3 font-sans">
              Teach your child<br />
              <span className="text-xl md:text-3xl lg:text-4xl">something new today</span>
            </h2>
            <p className="text-sm md:text-lg text-muted-foreground">
              Select an option to create a personalized story
            </p>
          </div>

          {/* Main Category Tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8 md:mb-10">
            {CATEGORIES.map(category => (
              <Card 
                key={category.id} 
                onClick={() => handleCategoryClick(category.id)} 
                className={`relative overflow-hidden h-36 md:h-48 cursor-pointer transition-all duration-200 hover:border-primary active:scale-[0.98] ${selectedCategory === category.id ? "border-primary shadow-md" : ""}`}
              >
                {!loadedImages.has(category.imageUrl) && (
                  <Skeleton className="absolute inset-0" />
                )}
                <div 
                  className={`absolute inset-0 bg-cover bg-center transition-opacity duration-300 ${loadedImages.has(category.imageUrl) ? 'opacity-100' : 'opacity-0'}`} 
                  style={{ backgroundImage: `url(${category.imageUrl})` }} 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                <div className="relative z-10 h-full flex items-end p-4 md:p-6">
                  <h3 className="text-lg md:text-2xl font-semibold text-white drop-shadow-lg font-sans">
                    {category.label}
                  </h3>
                </div>
              </Card>
            ))}
          </div>

          {/* Sub-option Tiles */}
          {selectedCategory && selectedCategoryData && (
            <div ref={subOptionsRef} className="mb-8 md:mb-10 animate-fade-in">
              <h3 className="text-lg md:text-2xl font-semibold text-foreground mb-3 md:mb-4 text-center font-sans">
                Choose a specific topic:
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                {selectedCategoryData.subOptions.map(subOption => (
                  <Card 
                    key={subOption.label} 
                    onClick={() => handleSubOptionClick(subOption)} 
                    className={`relative overflow-hidden h-28 md:h-32 cursor-pointer transition-all duration-200 hover:border-primary active:scale-[0.98] ${selectedSubOption === subOption.label ? "border-primary shadow-md" : ""}`}
                  >
                    {!loadedImages.has(subOption.imageUrl) && (
                      <Skeleton className="absolute inset-0" />
                    )}
                    <div 
                      className={`absolute inset-0 bg-cover bg-center transition-opacity duration-300 ${loadedImages.has(subOption.imageUrl) ? 'opacity-100' : 'opacity-0'}`} 
                      style={{ backgroundImage: `url(${subOption.imageUrl})` }} 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="relative z-10 h-full flex items-end p-2 md:p-3">
                      <p className="text-xs md:text-sm font-medium text-white drop-shadow-md">
                        {subOption.label}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Custom Details Form */}
          {selectedSubOption && <div ref={customFormRef} className="max-w-2xl mx-auto animate-fade-in">
              <Card className="p-4 md:p-6 space-y-4 md:space-y-5">
                {childName && currentPlan !== 'basic' && (
                  <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                    <Checkbox id="includeChild" checked={includeChild} onCheckedChange={checked => setIncludeChild(checked as boolean)} />
                    <Label htmlFor="includeChild" className="text-sm font-medium cursor-pointer">
                      Add {childName} as the main character
                    </Label>
                  </div>
                )}
                
                {currentPlan !== 'basic' && (
                  <div>
                    <label className="block text-sm md:text-base font-medium text-foreground mb-2">
                      Want to add anything specific to the story?
                    </label>
                    <Textarea value={customDetails} onChange={e => setCustomDetails(e.target.value)} placeholder="E.g., include my child's favorite toy, make it about a superhero, etc." className="min-h-[80px] md:min-h-[100px]" />
                  </div>
                )}
                
                <Button onClick={handleGenerateStory} size="lg" className="w-full gap-2 h-12 md:h-11 text-base">
                  <Sparkles className="w-5 h-5" />
                  Generate Story
                </Button>
              </Card>
            </div>}
        </div>
      </main>

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        currentPlan={currentPlan}
        storiesUsed={storiesUsed}
        limit={storyLimit}
        isExpired={isExpired}
      />
    </PageBackground>;
};
export default Home;